import { useCallback, useEffect, useState } from "react";
import { useBrainApi } from "../../api/brainApi";
import { Requirement } from "../../api/types";
import {
  PageTitle,
  Panel,
  AlertBanner,
  btnPrimary,
  btnSecondary,
} from "../../components/controlPlaneUi";

const STATUS_STYLE: Record<string, string> = {
  New: "text-[#9D9585] bg-[#262420]",
  Sent: "text-emerald-400 bg-emerald-950/40",
  Failed: "text-red-400 bg-red-950/40",
  Rejected: "text-[#9D9585] bg-[#262420]",
};

const RequirementsPage: React.FC = () => {
  const api = useBrainApi();
  const [reqs, setReqs] = useState<Requirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sending, setSending] = useState<number | null>(null);
  const [rejecting, setRejecting] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReqs(await api.getRequirements());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to load requirements");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const send = async (r: Requirement) => {
    setSending(r.id);
    setError(null);
    setNotice(null);
    try {
      const updated = await api.sendRequirement(r.id);
      setReqs((prev) => prev.map((x) => (x.id === r.id ? updated : x)));
      setNotice(`Sent "${updated.title}" to Airoplane as a pending work order.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "send failed");
    } finally {
      setSending(null);
    }
  };

  const reject = async (r: Requirement) => {
    setRejecting(r.id);
    setError(null);
    setNotice(null);
    try {
      const updated = await api.rejectRequirement(r.id);
      setReqs((prev) => prev.map((x) => (x.id === r.id ? updated : x)));
      setNotice(`Rejected "${updated.title}".`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "reject failed");
    } finally {
      setRejecting(null);
    }
  };

  return (
    <div>
      <PageTitle kind="Knowledge" name="requirements" />
      {error && <AlertBanner variant="error">{error}</AlertBanner>}
      {notice && <AlertBanner variant="ok">{notice}</AlertBanner>}

      <Panel title={`Requirements (${reqs.length})`}>
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : reqs.length === 0 ? (
          <p className="text-sm text-slate-500">
            No requirements yet — they appear after an ingestion run derives them.
          </p>
        ) : (
          <ul className="divide-y divide-[#262420]">
            {reqs.map((r) => (
              <li key={r.id} className="py-3 first:pt-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-sm text-[#E5E0D5]">{r.title}</span>
                      <span
                        className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
                          STATUS_STYLE[r.status] ?? STATUS_STYLE.New
                        }`}
                      >
                        {r.status}
                      </span>
                      {r.repo ? (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-[#262420] text-[#9D9585]">
                          {r.repo}
                        </span>
                      ) : (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700">
                          no target repo
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-[#9D9585] whitespace-pre-wrap">{r.body}</p>
                    {r.webhookSentAt && (
                      <div className="mt-1 text-[11px] text-slate-400">
                        sent {new Date(r.webhookSentAt).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-2">
                    {r.status !== "Rejected" && (
                      <button
                        className={btnPrimary}
                        disabled={
                          r.status === "Sent" || sending === r.id || rejecting === r.id || !r.repo
                        }
                        title={
                          r.status === "Sent"
                            ? "Already sent to Airoplane"
                            : !r.repo
                              ? "Held — assign a target repo before sending"
                              : `Send to Airoplane as a pending work order for ${r.repo}`
                        }
                        onClick={() => send(r)}
                      >
                        {r.status === "Sent"
                          ? "Sent ✓"
                          : sending === r.id
                          ? "Sending…"
                          : !r.repo
                            ? "Held (no repo)"
                            : "Send to Airoplane"}
                      </button>
                    )}
                    {r.status !== "Sent" && r.status !== "Rejected" && (
                      <button
                        className={btnSecondary}
                        disabled={sending === r.id || rejecting === r.id}
                        title="Reject this requirement — it won't be sent as a work order"
                        onClick={() => reject(r)}
                      >
                        {rejecting === r.id ? "Rejecting…" : "Reject"}
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
};

export default RequirementsPage;
